import { useCallback, useRef } from "react";
import { useReactToPrint } from "react-to-print";

const PRINT_PAGE_STYLE = `
  @media print {
    html, body {
      background-color: white !important;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      line-height: 1.6;
      padding: 20px;
    }
    img {
      page-break-inside: avoid;
      break-inside: avoid;
      max-width: 100%;
      max-height: 90vh;
    }
    pre, code {
      page-break-inside: avoid;
      break-inside: avoid;
      white-space: pre-wrap;
      word-wrap: break-word;
    }
    @page {
      margin: 1cm;
    }

    /* Prevent page breaks inside cards, tables, and chart containers */
    table, thead, tbody, tr {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }

    /* Cards - marked by data attribute in prepareForPrint */
    [data-print-card] {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }

    /* Keep headings with their following content */
    h1, h2, h3, h4, h5, h6 {
      page-break-after: avoid !important;
      break-after: avoid !important;
    }

    /* Ensure colors are preserved in print */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    /* Fix overflow containers to show all content */
    [data-print-overflow] {
      overflow: visible !important;
      max-height: none !important;
    }

    /* Ensure the print content has a white background */
    [data-print-content] {
      background-color: white !important;
    }

    /* Reveal collapsed accordion sections (keepContentMounted) so they print */
    .chakra-accordion__panel[hidden] {
      display: block !important;
    }

    /*
     * Panels revealed off-screen during onBeforePrint (so Recharts charts could
     * measure + paint) are pinned with position:absolute / a fixed width on the
     * live DOM. Drop them back into normal flow for the cloned print document.
     */
    [data-print-reveal] {
      position: static !important;
      top: auto !important;
      left: auto !important;
      width: auto !important;
      visibility: visible !important;
      display: block !important;
    }
  }
`;

// A zero page margin leaves the browser no room to print its date, title, URL and page number. Cloned padding keeps the inset on every page.
const NO_BROWSER_HEADERS_PAGE_STYLE = `
  @media print {
    @page {
      margin: 0;
    }
    body {
      padding: 0 !important;
    }
    [data-print-content] {
      padding: calc(1cm + 20px);
      box-decoration-break: clone;
      -webkit-box-decoration-break: clone;
    }
  }
`;

type ElementRestore = {
  element: HTMLElement;
  property: string;
  originalValue: string;
};

/**
 * Prepares the DOM for print by:
 * 1. Converting percentage-based widths to pixel widths (needed for bar charts)
 * 2. Ensuring padding is preserved from computed styles
 * 3. Marking cards and chart containers for page-break avoidance
 * 4. Marking overflow containers for CSS handling
 */
function prepareForPrint(container: HTMLElement): () => void {
  const elementsToRestore: ElementRestore[] = [];
  const markedCards: HTMLElement[] = [];
  const markedOverflow: HTMLElement[] = [];

  // Mark the container for white background in print
  container.setAttribute("data-print-content", "true");

  // Find all elements and check for percentage-based widths
  const allElements = container.querySelectorAll<HTMLElement>("*");

  allElements.forEach((element) => {
    // Skip hidden elements (e.g. inside collapsed sections): their computed
    // width is unreliable, so converting % widths here would pin them to 0px.
    if (element.offsetParent === null) {
      return;
    }

    const inlineWidth = element.style.width;
    const computedStyle = window.getComputedStyle(element);

    // Convert percentage widths to pixels for print
    if (inlineWidth && inlineWidth.includes("%")) {
      const computedWidth = computedStyle.width;

      elementsToRestore.push({
        element,
        property: "width",
        originalValue: inlineWidth,
      });
      elementsToRestore.push({
        element,
        property: "minWidth",
        originalValue: element.style.minWidth,
      });

      element.style.width = computedWidth;
      element.style.minWidth = computedWidth;
    }

    // Ensure padding is preserved for Card-like elements
    // Chakra UI uses CSS custom properties that may not persist in print
    const computedPadding = computedStyle.padding;

    if (computedPadding && computedPadding !== "0px") {
      const currentPadding = element.style.padding;
      if (!currentPadding || currentPadding.includes("var(")) {
        elementsToRestore.push({
          element,
          property: "padding",
          originalValue: currentPadding,
        });
        element.style.padding = computedPadding;
      }
    }

    // Mark card-like elements for page-break avoidance
    // Cards typically have border-radius, box-shadow, and background
    const hasBorderRadius =
      computedStyle.borderRadius && computedStyle.borderRadius !== "0px";
    const hasBoxShadow =
      computedStyle.boxShadow && computedStyle.boxShadow !== "none";
    const hasBackground =
      computedStyle.backgroundColor &&
      computedStyle.backgroundColor !== "rgba(0, 0, 0, 0)" &&
      computedStyle.backgroundColor !== "transparent";

    if (hasBorderRadius && (hasBoxShadow || hasBackground)) {
      element.setAttribute("data-print-card", "true");
      markedCards.push(element);
    }

    // Mark overflow containers
    if (
      element.style.overflow ||
      element.style.overflowX ||
      element.style.overflowY
    ) {
      element.setAttribute("data-print-overflow", "true");
      markedOverflow.push(element);
    }
  });

  // Return cleanup function
  return () => {
    // Remove the print content marker from container
    container.removeAttribute("data-print-content");

    elementsToRestore.forEach(({ element, property, originalValue }) => {
      element.style.setProperty(property, originalValue);
    });

    markedCards.forEach((element) => {
      element.removeAttribute("data-print-card");
    });

    markedOverflow.forEach((element) => {
      element.removeAttribute("data-print-overflow");
    });
  };
}

const PRINT_REVEAL_ATTR = "data-print-reveal";

/**
 * Waits for the given number of animation frames so the browser can run layout,
 * fire ResizeObserver callbacks, and let Recharts <ResponsiveContainer> repaint
 * before we hand the DOM to react-to-print for cloning.
 */
function waitForFrames(frames: number): Promise<void> {
  return new Promise((resolve) => {
    let remaining = frames;
    const step = () => {
      remaining -= 1;
      if (remaining <= 0) {
        resolve();
      } else {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  });
}

/**
 * Lays out collapsed `keepContentMounted` accordion panels off-screen so the
 * charts inside them paint before printing.
 *
 * A collapsed panel is `hidden` (display:none), so any Recharts
 * <ResponsiveContainer> inside it never measures a non-zero width and renders an
 * empty SVG — the print clone then captures nothing (the funnel/line/bar charts
 * come out blank). Heatmaps and DOM bar charts survive because they're plain
 * markup. We un-hide every collapsed panel (un-hiding ancestors too, so nested
 * charts get a layout box) and pin a real width off-screen, which forces those
 * charts to paint. The print stylesheet ([data-print-reveal]) drops them back
 * into normal flow for the actual PDF; this function returns a cleanup that
 * restores the live DOM afterward.
 */
function revealCollapsedSections(container: HTMLElement): () => void {
  const panels = Array.from(
    container.querySelectorAll<HTMLElement>(".chakra-accordion__panel[hidden]"),
  );

  if (panels.length === 0) {
    return () => {};
  }

  const containerWidth = container.getBoundingClientRect().width;

  const restores = panels.map((panel) => {
    const previous = {
      hidden: panel.hidden,
      position: panel.style.position,
      top: panel.style.top,
      left: panel.style.left,
      width: panel.style.width,
      visibility: panel.style.visibility,
    };

    // Use the panel's own row width when available (so nested/indented panels
    // paint at their real width); fall back to the report width when the panel
    // is nested inside another still-collapsed panel and can't be measured yet.
    const parentWidth = panel.parentElement?.getBoundingClientRect().width ?? 0;

    panel.hidden = false;
    panel.setAttribute(PRINT_REVEAL_ATTR, "true");
    panel.style.position = "fixed";
    panel.style.top = "0";
    panel.style.left = "-100000px";
    panel.style.visibility = "hidden";
    panel.style.width = `${parentWidth > 0 ? parentWidth : containerWidth}px`;

    return () => {
      panel.hidden = previous.hidden;
      panel.removeAttribute(PRINT_REVEAL_ATTR);
      panel.style.position = previous.position;
      panel.style.top = previous.top;
      panel.style.left = previous.left;
      panel.style.width = previous.width;
      panel.style.visibility = previous.visibility;
    };
  });

  return () => restores.forEach((restore) => restore());
}

type PrintOptions = {
  /** Runs once the print dialog closes, whether the user saved or cancelled. */
  onAfterPrint?: () => void;
  onPrintError?: (error: Error) => void;
  /** Leaves out the date, title, URL and page number that the browser prints in the page margins. */
  hideBrowserHeaders?: boolean;
};

export function usePrintToPdf(
  documentTitle = "Agent Response",
  options: PrintOptions = {},
) {
  const {
    onAfterPrint: afterPrint,
    onPrintError,
    hideBrowserHeaders,
  } = options;
  const contentRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const onBeforePrint = useCallback(async () => {
    const container = contentRef.current;
    if (!container) {
      return;
    }

    // Reveal collapsed sections first so charts inside them get a layout box,
    // then wait a few frames for Recharts to measure and paint before we run
    // the width/padding normalization and let react-to-print clone the DOM.
    const restoreReveal = revealCollapsedSections(container);

    try {
      await waitForFrames(3);
      const restorePrepare = prepareForPrint(container);

      cleanupRef.current = () => {
        restorePrepare();
        restoreReveal();
      };
    } catch (error) {
      // react-to-print does NOT call onAfterPrint when onBeforePrint rejects,
      // so undo the reveal here — otherwise the panels stay pinned off-screen
      // on the live DOM until a full reload. Re-throw so react-to-print aborts
      // instead of printing a half-prepared DOM.
      restoreReveal();
      cleanupRef.current = null;
      throw error;
    }
  }, []);

  const onAfterPrint = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    afterPrint?.();
  }, [afterPrint]);

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle,
    pageStyle: hideBrowserHeaders
      ? PRINT_PAGE_STYLE + NO_BROWSER_HEADERS_PAGE_STYLE
      : PRINT_PAGE_STYLE,
    onBeforePrint,
    onAfterPrint,
    onPrintError: onPrintError && ((_, error) => onPrintError(error)),
  });

  return { contentRef, handlePrint };
}
